/**
 * "What we can actually show you" — the proof band.
 *
 * Replaces the old SocialProof section, whose copy ("…are evaluating Serac")
 * was a written admission that nobody had committed, sitting in exactly the
 * slot where a comparing visitor looks for evidence.
 *
 * The shape of this section is deliberate: nothing here is asserted, every
 * line is a link the visitor can open and check in under a minute. Two rules
 * govern what may appear:
 *
 *   1. One live number, fetched — never typed. The GitHub star count comes
 *      from our own /api/github-stars proxy (server-cached 1h, so visitors
 *      don't share the GitHub 60/hr/IP limit). It is the only live figure
 *      the current infrastructure can serve to this page: the enforced CSP
 *      on dashboard.serac.build limits connect-src to 'self', and the apex
 *      (Cloudflare Pages, public/_headers) allows only dashboard.serac.build.
 *      So api.npmjs.org / api.github.com cannot be called from here without a
 *      backend proxy — no npm-downloads or forks figure until one exists.
 *      Nothing is hardcoded as a fallback: no count renders rather than a
 *      stale one.
 *
 *      ⚠ `proof.stats.forks`, `proof.stats.lastPush` and
 *      `proof.stats.downloads` therefore have nothing to render and are read
 *      by nothing in this file. They are reachable only behind three more
 *      backend proxies alongside /api/github-stars. Until those exist, copy
 *      that promises figures "from GitHub and npm" overstates what paints —
 *      the honest sentence covers one live GitHub figure. That sentence lives
 *      in `proof.body` and belongs to the copy owner, not to this file.
 *
 *   2. Everything else is an { claim, verifyLabel, verifyHref } row from the
 *      `proof.items` array in locales/{en,nl}/marketing.json. An item without
 *      a working link does not belong in this section and is dropped here at
 *      render time if it arrives without one.
 *
 * All copy lives in the `proof` namespace. This file holds no user-visible
 * English apart from the repository slug, which is a proper noun and is
 * already rendered verbatim elsewhere on the page (finalCTA.githubRepo).
 *
 * The day a named customer agrees to be quoted, that quote replaces this
 * whole band.
 */
import { useEffect, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../serac';
import { useIsMobile } from '../../hooks/useMediaQuery';

const REPO_URL = 'https://github.com/serac-labs/serac';
const REPO_SLUG = 'serac-labs/serac';

interface ProofItem {
  claim: string;
  verifyLabel: string;
  verifyHref: string;
}

/**
 * i18next returns the raw key string when a key is missing, and the shape of
 * `proof.items` is only as good as the locale file. Coerce defensively: an
 * item without a claim or without a link is dropped rather than rendered as
 * a dead row.
 */
function toProofItems(raw: unknown): ProofItem[] {
  const list = Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? Object.values(raw) : [];
  return list.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return [];
    const { claim, verifyLabel, verifyHref } = entry as Record<string, unknown>;
    if (typeof claim !== 'string' || claim.length === 0) return [];
    if (typeof verifyHref !== 'string' || verifyHref.length === 0) return [];
    if (typeof verifyLabel !== 'string' || verifyLabel.length === 0) return [];
    return [{ claim, verifyLabel, verifyHref }];
  });
}

/**
 * Live star count off our own proxy. Mirrors SeracTopNav.tsx: the apex host
 * (serac.build on Cloudflare Pages) has no backend, so it reaches the API on
 * dashboard.serac.build — the one cross-origin the marketing CSP permits.
 * Deferred to idle so it never sits on the marketing critical path; on
 * failure we render the repo link without a number.
 */
function useGithubStars(): string | null {
  const [stars, setStars] = useState<string | null>(null);

  useEffect(() => {
    const isApex =
      typeof window !== 'undefined' && /^(www\.)?serac\.build$/.test(window.location.hostname);
    const starsUrl = isApex ? 'https://dashboard.serac.build/api/github-stars' : '/api/github-stars';

    let cancelled = false;
    const idle =
      (typeof window !== 'undefined' &&
        (window as Window & { requestIdleCallback?: typeof requestIdleCallback })
          .requestIdleCallback) ||
      ((cb: () => void) => setTimeout(cb, 1500));

    const handle = idle(() => {
      fetch(starsUrl)
        .then((r) => (r.ok ? r.json() : null))
        .then((data: { success?: boolean; formatted?: string } | null) => {
          if (cancelled) return;
          if (data?.success && typeof data.formatted === 'string') setStars(data.formatted);
        })
        .catch(() => {});
    });

    return () => {
      cancelled = true;
      const cancel =
        (typeof window !== 'undefined' &&
          (window as Window & { cancelIdleCallback?: typeof cancelIdleCallback })
            .cancelIdleCallback) ||
        clearTimeout;
      cancel(handle as number);
    };
  }, []);

  return stars;
}

export default function SectionProof() {
  const { t, i18n } = useTranslation('marketing');
  const isMobile = useIsMobile();
  const stars = useGithubStars();

  const items = toProofItems(t('proof.items', { returnObjects: true }));

  // "★ 78" is not self-describing to a screen reader, so the figure carries
  // the locale's own label for it. Optional: if the key is absent the number
  // renders unlabelled rather than as a raw key name.
  const starsLabel = i18n.exists('proof.stats.stars', { ns: 'marketing' })
    ? t('proof.stats.stars')
    : undefined;

  // The verify labels are prose, not slugs ("The TCO of AI in a ServiceNow
  // practice"), and the items wrapper clips its children so the border radius
  // holds. Keeping the label on one line is right on desktop, where the row is
  // two columns and the claim yields the space — but on a 320px screen the row
  // is a single column ~256px wide and a nowrap label is silently cut off with
  // no way to scroll to the rest of it. So: wrap below the mobile breakpoint.
  const verifyStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: isMobile ? 'flex-start' : 'center',
    gap: 6,
    maxWidth: '100%',
    flexShrink: 0,
    fontFamily: 'var(--font-mono)',
    fontSize: 12,
    letterSpacing: '0.02em',
    lineHeight: 1.45,
    color: 'var(--ice-400)',
    textDecoration: 'none',
    whiteSpace: isMobile ? 'normal' : 'nowrap',
    transition: 'color 150ms',
  };

  return (
    <section
      id="proof"
      aria-labelledby="proof-headline"
      style={{ padding: isMobile ? '56px 16px' : '80px 32px', borderTop: '1px solid var(--border)' }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div className="t-eyebrow" style={{ marginBottom: 16 }}>
          {t('proof.eyebrow')}
        </div>
        <h2
          id="proof-headline"
          className="t-display"
          style={{
            fontSize: 'clamp(28px, 5vw, 44px)',
            margin: '0 0 12px',
            maxWidth: 700,
            lineHeight: 1.15,
          }}
        >
          {t('proof.headline')}
        </h2>
        <p
          style={{
            fontSize: 16,
            color: 'var(--fg-muted)',
            lineHeight: 1.6,
            maxWidth: 640,
            margin: 0,
          }}
        >
          {t('proof.body')}
        </p>

        {/* The one live figure on the page that isn't typed into a locale
            file. Small on purpose — the honesty is the point, not the size. */}
        <a
          href={REPO_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            marginTop: isMobile ? 24 : 28,
            padding: '8px 14px',
            border: '1px solid var(--border)',
            borderRadius: 999,
            background: 'var(--ink-050)',
            color: 'var(--fg-muted)',
            fontFamily: 'var(--font-mono)',
            fontSize: 13,
            textDecoration: 'none',
            transition: 'border-color 160ms, color 160ms',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--border-strong)';
            e.currentTarget.style.color = 'var(--fg)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--border)';
            e.currentTarget.style.color = 'var(--fg-muted)';
          }}
        >
          <Icon.github s={14} />
          <span>{REPO_SLUG}</span>
          {stars !== null && (
            <>
              <span aria-hidden="true" style={{ color: 'var(--border-strong)' }}>
                |
              </span>
              <span style={{ color: 'var(--fg)' }} aria-label={starsLabel}>
                ★ {stars}
              </span>
            </>
          )}
        </a>

        {items.length > 0 && (
          <div
            style={{
              marginTop: isMobile ? 28 : 36,
              border: '1px solid var(--border)',
              borderRadius: 12,
              background: 'var(--ink-050)',
              overflow: 'hidden',
            }}
          >
            {items.map((item, i) => {
              const isInternal = item.verifyHref.startsWith('/');
              const label = (
                <>
                  <span>{item.verifyLabel}</span>
                  <span
                    style={{
                      display: 'inline-flex',
                      flexShrink: 0,
                      paddingTop: isMobile ? 3 : 0,
                    }}
                  >
                    {isInternal ? <Icon.arrow s={12} /> : <Icon.external s={12} />}
                  </span>
                </>
              );

              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    justifyContent: 'space-between',
                    gap: isMobile ? 10 : 24,
                    padding: isMobile ? '18px 16px' : '18px 24px',
                    borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                  }}
                >
                  <div
                    style={{
                      minWidth: 0,
                      fontSize: 15,
                      color: 'var(--fg)',
                      lineHeight: 1.55,
                      letterSpacing: '-0.005em',
                    }}
                  >
                    {item.claim}
                  </div>
                  {isInternal ? (
                    <Link
                      to={item.verifyHref}
                      style={verifyStyle}
                      aria-label={`${item.verifyLabel}: ${item.claim}`}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'var(--ice-300)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'var(--ice-400)';
                      }}
                    >
                      {label}
                    </Link>
                  ) : (
                    <a
                      href={item.verifyHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={verifyStyle}
                      aria-label={`${item.verifyLabel}: ${item.claim}`}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'var(--ice-300)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'var(--ice-400)';
                      }}
                    >
                      {label}
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
